import { VSTR_AUDIO_UPLOAD_MIME_PROFILE } from "@frontend-shared/constants";
import {
  resolveUploadMimeType,
  validateUploadFile,
} from "@frontend-shared/utils";
import { useCallback } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";

import { useMeetingUploads } from "@/hooks/use-meeting-uploads";
import type { IUploadFileParams } from "@/pages/uploads/types";

// NOTE: NSAI-7067 — validate a meeting recording and, if it passes, hand it to
// the app-level uploader. Two callers: the Uploads page's dropzone, and the
// TransferProvider's connector hand-off, which since this ticket runs above the
// routes instead of inside that page. One hook so a file pulled out of Google
// Drive is refused for exactly the reasons — and in exactly the words — a file
// dropped on the page is.
//
// NOTE: The boolean is the integration picker's "was this queued?" signal — a
// validation rejection must not read as a started upload (NSAI-5134).
export const useValidatedMeetingUpload = (): ((
  props: IUploadFileParams,
) => boolean) => {
  const { t } = useTranslation("uploads");
  const { uploadFile } = useMeetingUploads();

  return useCallback(
    ({ file, transferId }: IUploadFileParams): boolean => {
      // NOTE: NSAI-7594 — canonicalise before validating, as mobile has since
      // NSAI-4873. `file.type` is the browser's answer, not ours — Chromium
      // reports `.mkv` as `video/matroska`, a spelling the allow-list does not
      // carry — and a file fetched from a connected app arrives typed by its
      // provider. Compared raw, both were refused here while the dropzone
      // advertised the format.
      const mimeType = resolveUploadMimeType({
        fileName: file.name,
        profile: VSTR_AUDIO_UPLOAD_MIME_PROFILE,
        pickedMimeType: file.type,
      });
      const validationResult = validateUploadFile({
        mimeType,
        size: file.size,
      });
      if (!validationResult.isValid) {
        if (validationResult.errorKey) {
          // NOTE: NSAI-4891 — `errorParams` carries the resolved cap and the
          // file's own size, so the rejection names both instead of reading
          // "File is too large." and leaving the user to guess by how much.
          toast.error(
            t(validationResult.errorKey, validationResult.errorParams),
          );
        }
        return false;
      }

      // NOTE: NSAI-5560 — the uploader turns away a file it is already
      // uploading, and that refusal is not a queued upload: reporting it as one
      // would close the integration picker over a file that never started.
      return uploadFile({
        file,
        fileName: file.name,
        mimeType,
        ...(transferId !== undefined && { transferId }),
      });
    },
    [t, uploadFile],
  );
};
